/**
 * meetingContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import React from "react";
import { useParams } from "react-router-dom";
import OT from "@opentok/client";

import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import { AuthContext } from "../auth/auth.context";

import { CaptureCredentialsComponent } from "./captureCredentials.component";
import { OnboardMeetingComponent } from "../auth/onboardMeeting.component";
import { WelcomeMatComponent } from "./welcomeMat.component";
import { MeetingInfoComponent } from "./meetingInfo.component";
import { MeetingSessionComponent } from "./meetingSession.component";

import { Meeting } from "../models/meeting";
import { Membership } from "../models/auth";

import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
import { dbServiceFactory } from "../services/db.service";
import { groupServiceFactory } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";
import { ChannelMeetingMessage } from "../models/broadcast";



interface WrapperProps {
  meeting: Meeting | null,
  setMeeting(meeting: Meeting | null): void,
  leaveMeeting(): void
}
export function MeetingContainerWrapper(props: WrapperProps): React.ReactElement<any, any> {
  const { meetingID, meetingToken } = useParams<{ meetingID: string, meetingToken: string }>();
  return <MeetingContainer {...props} meetingID={meetingID} meetingToken={meetingToken} />;
}

interface Props extends WrapperProps {
  meetingID: string | undefined,
  meetingToken: string | undefined
}
interface State {
  session: OT.Session | null,
  token: string,
  enableAudio: boolean,
  enableVideo: boolean,
  loading: boolean,
  joining: boolean,
  waiting: boolean,
  meetingStarted: boolean,
  ended: boolean,
  isHost: boolean,
  videoSrcID: string,
  audioSrcID: string,
  openInfoModal: boolean
}

export class MeetingContainer extends React.Component<Props, State> {
  static contextType: React.Context<AuthContext> = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;

  private pollingTimer: NodeJS.Timeout | null;

  constructor(props: Props) {
    super(props);
    this.pollingTimer = null;

    this.waitingResultCB = this.waitingResultCB.bind(this);
    this.joinRoom = this.joinRoom.bind(this);
    this.leaveRoom = this.leaveRoom.bind(this);
    this.closeWelcomeMat = this.closeWelcomeMat.bind(this);
    this.didFinishLogin = this.didFinishLogin.bind(this);
    this.didCreateTempUser = this.didCreateTempUser.bind(this);
    this.setAudioSource = this.setAudioSource.bind(this);
    this.setVideoSource = this.setVideoSource.bind(this);

    this.state = {
      session: null,
      token: "",
      enableAudio: true,
      enableVideo: true,
      loading: false,
      joining: false,
      waiting: false,
      meetingStarted: false,
      ended: false,
      isHost: false,
      videoSrcID: "",
      audioSrcID: "",
      openInfoModal: false
    };
  }

  componentDidMount() {
    const authContext = this.context as AuthContext;
    const workerService = workerServiceFactory();
    workerService.on("mtgDecision", this.waitingResultCB);

    this.setState({
      loading: true,
      isHost: this.props.meeting !== null &&
        authContext.membership !== null &&
        this.props.meeting.hostUserIDs.includes(authContext.membership.userID),
      waiting: false,
      meetingStarted: false
    });
  }

  componentWillUnmount() {
    const workerService = workerServiceFactory();
    workerService.off("mtgDecision", this.waitingResultCB);
    this.state.session?.disconnect();
    workerService.didFinishMeetingSession();
  }

  async componentDidUpdate(prevProps: Props) {
    const authContext = this.context as AuthContext;

    const isHost = this.props.meeting !== null &&
      authContext.membership !== null &&
      this.props.meeting.hostUserIDs.includes(authContext.membership.userID);

    if (this.state.isHost !== isHost) {
      this.setState({ isHost: isHost });
    }

    if (this.props.meetingID && this.props.meetingToken &&
      (!this.props.meeting || this.props.meetingID !== prevProps.meetingID)) {
      const meetingID = this.props.meetingID;
      const meetingToken = this.props.meetingToken;
      const userID = authContext.membership?.userID || "";
      try {
        const [meeting, group] = await bluebird.all([
          conferenceServiceFactory().getMeeting(meetingID, meetingToken),
          dbServiceFactory().getGroup(meetingID)
        ]);
        if (!meeting) {
          this.props.setMeeting(null);
          return;
        }
        meeting.group = group;
        if (authContext.access_token && (!authContext.membership ||
          authContext.membership && authContext.membership.accountID !== meeting.accountID)) {
          const authService = authServiceFactory();
          const memberships = await authService.getAccountMemberships();
          const member = memberships.find(m => m.accountID === meeting.accountID);

          if (member) {
            authService.currentAccountMembership = member;
            await authContext.setAccount(member);
          } else {
            // clear the account, we'll create a temp one when we join the meeting
            authService.currentAccountMembership = null;
            await authContext.setAccount(null);
          }
        }
        this.props.setMeeting(meeting);
        if (userID) {
          this.setState({ isHost: meeting.hostUserIDs.includes(userID) });
        } else {
          this.setState({ isHost: false });
        }
        this.setState({ waiting: false, meetingStarted: false, loading: false });
      } catch (err) {
        console.error("Error getting meeting or group:", err);
      }
    }
  }

  async waitingResultCB(mtgMsg: ChannelMeetingMessage): Promise<void> {
    try {
      if (mtgMsg.allowed) {
        await this.joinRoom(true, undefined, this.state.videoSrcID, this.state.audioSrcID);
      }
    } catch (err) {
      console.error("Error joining meeting:", err);
    }
  }

  async joinRoom(isRouted: boolean, _notifyMembers: Array<Membership> | undefined,
    videoSrcID: string, audioSrcID: string): Promise<void> {
    const authContext = this.context as AuthContext;
    const authService = authServiceFactory();
    const meeting = this.props.meeting;
    if (!meeting) {
      console.log("no meeting context");
      return;
    }
    try {
      if (this.state.joining) {
        console.log("skipping joining:", this.state.joining, "mem:", authContext.membership);
        return;
      }
      this.setState({ joining: true, videoSrcID: videoSrcID, audioSrcID: audioSrcID });
      let membership: Membership | null;
      if (authContext.membership) {
        membership = authContext.membership;
      } else {
        console.log("creating temp membership for logged in user");
        const mem = await authService.createTempMember(meeting.accountID, meeting._id, meeting.token);
        console.log("new membership:", mem);
        await authContext.setAccount(mem);
        membership = mem;
      }
      if (!membership) {
        console.error("membership still null after temp member creation");
        return; // make TS happy
      }

      const groupService = groupServiceFactory();
      if (!groupService.deviceID) {
        console.error("deviceID not set on groupService. Did init get called?");
        return;
      }

      const result = await conferenceServiceFactory().joinMeeting(
        membership.accountID, meeting._id, groupService.deviceID, isRouted);

      workerServiceFactory().didStartMeetingSession();

      if (result.queued) {
        this.setState({ waiting: true, meetingStarted: result.started });

        // 30s is sufficient. We just want to keep the service worker active.
        this.pollingTimer = setInterval(() => {
          this.pollJoin(meeting, groupService.deviceID);
        }, 30000);
        return;
      }
      if (!result.sessionID || !result.token) {
        console.error("Bad result from server");
        if (this.pollingTimer) { clearInterval(this.pollingTimer); this.pollingTimer = null; }
        return;
      }
      const session = OT.initSession(authServiceFactory().OPENTOK_API_KEY, result.sessionID);
      this.setState({
        session: session,
        token: result.token,
        openInfoModal: this.state.isHost
      });
      if (this.pollingTimer) { clearInterval(this.pollingTimer); this.pollingTimer = null; }
    } catch (err) {
      console.error("Error joining:", err);
    } finally {
      this.setState({ joining: false });
    }
  }

  // Polling keeps our service worker alive testing our fetch() request,
  // side effect is that our socket stays open, so the accept/decline signal
  // is delivered immediately
  async pollJoin(meeting: Meeting, deviceID: string) {
    try {
      const membership = (this.context as AuthContext).membership;
      if (!membership) { return; }
      const result = await conferenceServiceFactory().pollWaiting(
        membership.accountID, meeting._id, deviceID);

      workerServiceFactory().didStartMeetingSession();

      if (result.queued) {
        this.setState({ waiting: true, meetingStarted: result.started });
        return;
      }

      if (this.pollingTimer) { clearInterval(this.pollingTimer); this.pollingTimer = null; }

      if (!result.sessionID || !result.token) {
        console.error("Bad result from server");
        return;
      }
      const session = OT.initSession(authServiceFactory().OPENTOK_API_KEY, result.sessionID);
      this.setState({
        session: session,
        token: result.token,
        openInfoModal: this.state.isHost
      });
    } catch (err) {
      console.error("Error polling join:", err);
    }
  }

  async leaveRoom(): Promise<void> {
    this.state.session?.disconnect();
    workerServiceFactory().didFinishMeetingSession();
    try {
      const membership = this.context.membership;
      const meeting = this.props.meeting;
      const groupService = groupServiceFactory();
      if (membership && meeting && groupService.deviceID) {
        await conferenceServiceFactory().leaveMeeting(
          membership.accountID, meeting._id, groupService.deviceID);
      }
      this.updateEndMeetingState();
    } catch (err) {
      console.error("Error leaving meeting:", err);
    }
  }

  updateEndMeetingState() {
    if (authServiceFactory().needAuthMethod) {
      this.setState({ ended: true });
    } else {
      this.props.leaveMeeting();
    }
  }

  didFinishLogin() {
    const authContext = this.context as AuthContext;
    return authContext.finishedLogin();
  }
  didCreateTempUser() {
    const authContext = this.context as AuthContext;
    return authContext.finishedLogin();
  }

  async closeWelcomeMat() {
    try {
      if (this.state.waiting) {
        const authContext = this.context as AuthContext;
        const membership = authContext.membership;
        const meeting = this.props.meeting;
        const groupService = groupServiceFactory();
        if (membership && meeting && groupService.deviceID) {
          await conferenceServiceFactory().leaveMeeting(
            membership.accountID, meeting._id, groupService.deviceID);
        }
      }
      if (this.pollingTimer) { clearInterval(this.pollingTimer); this.pollingTimer = null; }
      this.updateEndMeetingState();
    } catch (err) {
      console.error("Error leaving waiting room:", err);
    }
  }

  setVideoSource(srcID: string) {
    this.setState({ videoSrcID: srcID });
  }
  setAudioSource(srcID: string) {
    this.setState({ audioSrcID: srcID });
  }

  render(): React.ReactElement<any, any> {
    const authContext = this.context as AuthContext;

    if (this.state.loading) {
      return <div className="main-section-container meeting-container">
        <div className="main-section-inner">
          <CircularProgress />
        </div>
      </div>;
    }
    if (!this.props.meeting) {
      return <div className="main-section-container meeting-container">
        <div className="main-section-inner">
          <h1 className="welcome-heading">Meeting not Found</h1>
          <Alert severity="error">
            We couldn't find this meeting. You may have been given a faulty link,
            or it may not exist. Please, contact your meeting organizer.
          </Alert>
        </div>
      </div>;
    }

    if (this.state.ended) {
      return <div className="main-section-container meeting-container">
        <CaptureCredentialsComponent />
      </div>
    }

    if (!authContext.access_token) {
      return <div className="main-section-container meeting-container">
        <OnboardMeetingComponent
          accountID={this.props.meeting.accountID}
          meeting={this.props.meeting}
          didLoginUser={this.didFinishLogin}
          didCreateTempUser={this.didCreateTempUser} />
      </div>;
    }

    return <div className="main-section-container both-side-margin meeting-container">
      <div className="main-scroll-container">
        {this.state.session && this.props.meeting ?
          <MeetingSessionComponent
            meeting={this.props.meeting}
            session={this.state.session}
            token={this.state.token}
            isHost={this.state.isHost}
            sendAudio={this.state.enableAudio}
            sendVideo={this.state.enableVideo}
            videoSrcID={this.state.videoSrcID}
            audioSrcID={this.state.audioSrcID}
            setVideoSourceID={this.setVideoSource}
            setAudioSourceID={this.setAudioSource}
            onToggleAudio={() => this.setState({ enableAudio: !this.state.enableAudio })}
            onToggleVideo={() => this.setState({ enableVideo: !this.state.enableVideo })}
            disabledAudio={() => this.setState({ enableAudio: false })}
            onLeaveRoom={this.leaveRoom} /> :
          <>
            <header className="iris-header meeting-list-header">
              <div className="logo-img" />
            </header>
            <WelcomeMatComponent
              onJoin={this.joinRoom}
              onClose={this.closeWelcomeMat}
              sendAudio={this.state.enableAudio}
              sendVideo={this.state.enableVideo}
              hideSessionOptions={!this.state.isHost}
              isWaiting={this.state.waiting}
              meetingHasStarted={this.state.meetingStarted}
              proceedText={this.state.isHost ? "Launch" : undefined}
              onToggleAudio={() => this.setState({ enableAudio: !this.state.enableAudio })}
              onToggleVideo={() => this.setState({ enableVideo: !this.state.enableVideo })}
            />
          </>}
        {this.props.meeting && this.state.openInfoModal && <MeetingInfoComponent
          meeting={this.props.meeting}
          open={this.state.openInfoModal}
          onClose={() => this.setState({ openInfoModal: false })} />}
      </div>
    </div>;
  }
}
